/* Exact Shelby tails lettering, with visible-ink measurements in millimeters. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./je-script-name-model.js'));
  else root.ScriptNameRenderer = factory(root.ScriptNameModel);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (M) {
  'use strict';
  const SIZE = 512, DPI = 600, FAMILY = 'Jax Script Name Shelby';
  const SHA256 = '5c2cc8c54f6a00934ae3b1f191fb2d5105a11d4c7fce3c0d6d6668eb91a7a539';
  function canvas(width, height) {
    const output = document.createElement('canvas'); output.width = Math.ceil(width); output.height = Math.ceil(height); return output;
  }
  function fontStyle(ctx) {
    ctx.font = `400 ${SIZE}px "${FAMILY}"`; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.fontKerning = 'normal'; ctx.fillStyle = '#000';
  }
  async function load(options = {}) {
    if (!M) throw new Error('The lettering tools did not load. Refresh to retry.');
    const entry = options.font || options.fonts?.script, value = typeof entry === 'string' ? entry : entry?.url;
    if (typeof value !== 'string' || !value.trim()) throw new TypeError('Provide the original Shelby font URL.');
    const url = new URL(value, document.baseURI);
    if (!['https:', 'http:'].includes(url.protocol)) throw new TypeError('Use an HTTP font URL.');
    let timer;
    try {
      const font = new FontFace(FAMILY, `url(${JSON.stringify(url.href)})`, { weight: '400', style: 'normal' });
      await Promise.race([font.load(), new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Font loading timed out.')), 15000); })]);
      document.fonts.add(font);
    } catch { throw new Error('Could not load the script lettering. Try loading again.'); }
    finally { clearTimeout(timer); }
    const metrics = new Map();
    function measure(name) {
      const error = M.textError(name); if (error) throw new RangeError(error);
      if (metrics.has(name)) return metrics.get(name);
      const shaped = M.renderText(name), ctx = canvas(1, 1).getContext('2d', { willReadFrequently: true }); fontStyle(ctx);
      const measured = ctx.measureText(shaped);
      let xMin = -measured.actualBoundingBoxLeft, xMax = measured.actualBoundingBoxRight;
      let yMin = -measured.actualBoundingBoxAscent, yMax = measured.actualBoundingBoxDescent;
      if (![xMin, xMax, yMin, yMax].every(Number.isFinite) || xMax <= xMin || yMax <= yMin) throw new RangeError('The name needs visible lettering.');
      // Match EtsyApp's native glyph shaping and alpha-bounds measurement.
      const ox = Math.floor(xMin) - 4, oy = Math.floor(yMin) - 4, rawWidth = Math.ceil(xMax) - ox + 4, rawHeight = Math.ceil(yMax) - oy + 4;
      const resolution = Math.min(4, 8192 / rawWidth, 4096 / rawHeight, Math.sqrt(8000000 / (rawWidth * rawHeight)));
      const probe = canvas(rawWidth * resolution, rawHeight * resolution), pc = probe.getContext('2d', { willReadFrequently: true });
      pc.scale(resolution, resolution); fontStyle(pc); pc.fillText(shaped, -ox, -oy);
      const pixels = pc.getImageData(0, 0, probe.width, probe.height).data;
      let left = probe.width, right = -1, top = probe.height, bottom = -1;
      for (let index = 3; index < pixels.length; index += 4) {
        if (pixels[index] < 32) continue;
        const p = (index - 3) / 4, x = p % probe.width, y = Math.floor(p / probe.width);
        left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y);
      }
      if (right < left || bottom < top) throw new RangeError('The name needs visible lettering.');
      xMin = ox + left / resolution; xMax = ox + (right + 1) / resolution;
      yMin = oy + top / resolution; yMax = oy + (bottom + 1) / resolution;
      const result = Object.freeze({ shaped, xMin, xMax, yMin, yMax, width: yMax - yMin, height: xMax - xMin,
        horizontalWidth: xMax - xMin, horizontalHeight: yMax - yMin, aspect: (yMax - yMin) / (xMax - xMin), angle: -90 });
      if (metrics.size >= 200) metrics.clear(); metrics.set(name, result); return result;
    }
    function createDesign(seed) {
      if (!seed || typeof seed !== 'object') throw new RangeError('A design is required.');
      const sizeOz = Number(seed.sizeOz), board = M.boardForSize(sizeOz), targetHeight = Number(seed.targetHeight ?? M.DEFAULT_HEIGHT);
      if (!Number.isFinite(targetHeight) || targetHeight <= 0 || targetHeight > M.MAX_HEIGHT) throw new RangeError('Requested artwork height must be greater than zero and no more than 170 mm.');
      const text = measure(seed.name), factor = Math.min(targetHeight / text.height, M.MAX_WIDTH / text.width);
      const width = text.width * factor, height = text.height * factor;
      return { version: 1, designType: 'script-name', id: String(seed.id || 'script-name-design'), name: seed.name, sizeOz, targetHeight, board,
        layers: { name: { x: (board.width - width) / 2, y: (board.height - height) / 2, width, height } } };
    }
    function validate(design) {
      const errors = M.validate(design); if (errors.length) return errors;
      const text = measure(design.name), n = design.layers.name;
      if (Math.abs(n.width / n.height / text.aspect - 1) > .001) errors.push('The name proportions have changed. Reset its size to restore the original lettering.');
      return errors;
    }
    function assertValid(design) { const errors = validate(design); if (errors.length) throw new RangeError(errors.join(' ')); }
    function paint(ctx, design, scale) {
      if (!Number.isFinite(scale) || scale <= 0) throw new RangeError('A positive pixels-per-millimeter scale is required.');
      const text = measure(design.name), n = design.layers.name, textScale = n.width / text.width;
      ctx.save(); ctx.scale(scale, scale); ctx.translate(n.x - text.yMin * textScale, n.y + text.xMax * textScale);
      ctx.rotate(-Math.PI / 2); ctx.scale(textScale, textScale); fontStyle(ctx); ctx.fillText(text.shaped, 0, 0); ctx.restore();
    }
    function productionSpec(design, dpi = DPI) {
      assertValid(design);
      if (!Number.isFinite(dpi) || dpi < 72 || dpi > 1200) throw new RangeError('Choose a DPI from 72 to 1200.');
      const { width, height } = design.board, scale = dpi / 25.4;
      return { physicalArea: { widthMm: width, heightMm: height }, region: { x: 0, y: 0, width, height }, dpi, scale,
        pixelWidth: Math.round(width * scale), pixelHeight: Math.round(height * scale), filename: `script-name-back-${width}x${height}mm-${dpi}dpi.png` };
    }
    function renderCanvas(design, settings = {}) {
      assertValid(design);
      const scale = settings.scale ?? 4, color = settings.color ?? '#000';
      if (!Number.isFinite(scale) || scale <= 0 || scale > 1200 / 25.4) throw new RangeError('Choose a supported preview scale.');
      if (typeof color !== 'string' || !CSS.supports('color', color)) throw new RangeError('Choose a valid ink color.');
      const output = canvas(Math.max(1, Math.round(design.board.width * scale)), Math.max(1, Math.round(design.board.height * scale)));
      const ctx = output.getContext('2d', { willReadFrequently: true }); paint(ctx, design, scale);
      ctx.globalCompositeOperation = 'source-in'; ctx.fillStyle = color; ctx.fillRect(0, 0, output.width, output.height); ctx.globalCompositeOperation = 'source-over';
      return output;
    }
    function place(design, settings = {}) {
      assertValid(design);
      const region = { ...settings.region }, physicalArea = { ...settings.physicalArea }, verticalPlacement = settings.verticalPlacement ?? 'center';
      if (![region.x, region.y, region.width, region.height, physicalArea.widthMm, physicalArea.heightMm].every(Number.isFinite) ||
          region.width <= 0 || region.height <= 0 || physicalArea.widthMm <= 0 || physicalArea.heightMm <= 0 || !['center', 'optical'].includes(verticalPlacement)) {
        throw new RangeError('A valid calibrated region and physical area are required.');
      }
      const b = M.bounds(design);
      if (design.board.width > physicalArea.widthMm + .001 || design.board.height > physicalArea.heightMm + .001) throw new RangeError('The design board does not fit this physical engraving area.');
      const centeredX = (physicalArea.widthMm - design.board.width) / 2, centeredY = (physicalArea.heightMm - design.board.height) / 2;
      const verticalOffsetMm = verticalPlacement === 'optical' ? -Math.min(10, Math.max(0, centeredY + b.yMin)) : 0;
      const physicalPlacement = { x: centeredX, y: centeredY + verticalOffsetMm };
      const xScale = region.width / physicalArea.widthMm, yScale = region.height / physicalArea.heightMm;
      const x = region.x + physicalPlacement.x * xScale, y = region.y + physicalPlacement.y * yScale;
      const mapped = { xMin: x + b.xMin * xScale, yMin: y + b.yMin * yScale, xMax: x + b.xMax * xScale, yMax: y + b.yMax * yScale };
      return { version: 1, id: design.id, units: 'mm', board: { ...design.board }, region, physicalArea,
        widthMm: b.width, heightMm: b.height, verticalPlacement, verticalOffsetMm, physicalPlacement,
        physicalBounds: { xMin: b.xMin + physicalPlacement.x, yMin: b.yMin + physicalPlacement.y, xMax: b.xMax + physicalPlacement.x,
          yMax: b.yMax + physicalPlacement.y, widthMm: b.width, heightMm: b.height },
        placement: { x, y, xScale, yScale, transform: `translate(${x} ${y}) scale(${xScale} ${yScale})` }, bounds: mapped,
        fits: mapped.xMin >= region.x - .001 && mapped.yMin >= region.y - .001 && mapped.xMax <= region.x + region.width + .001 && mapped.yMax <= region.y + region.height + .001 };
    }
    async function exportPng(design, settings = {}) {
      const spec = productionSpec(design), output = canvas(spec.pixelWidth, spec.pixelHeight), ctx = output.getContext('2d', { willReadFrequently: true });
      if (settings.verticalPlacement !== undefined) {
        const placement = place(design, { ...spec, verticalPlacement: settings.verticalPlacement });
        ctx.translate(placement.physicalPlacement.x * spec.scale, placement.physicalPlacement.y * spec.scale);
      }
      paint(ctx, design, spec.scale);
      const blob = await new Promise((resolve, reject) => output.toBlob(value => value ? resolve(value) : reject(new Error('PNG export failed.')), 'image/png'));
      return new Blob([withDpi(new Uint8Array(await blob.arrayBuffer()), DPI)], { type: 'image/png' });
    }
    function fitInfo(design) {
      assertValid(design);
      const text = measure(design.name), n = design.layers.name;
      return { requestedHeight: design.targetHeight, actualHeight: n.height, actualWidth: n.width,
        widthLimited: text.aspect * design.targetHeight > M.MAX_WIDTH + .001 && n.width >= M.MAX_WIDTH - .001 };
    }
    return Object.freeze({ measure, createDesign, validate, paint, productionSpec, renderCanvas, place, exportPng, fitInfo,
      assetInfo: Object.freeze({ version: 1, font: 'Shelby tails', sha256: SHA256, dpi: DPI, rotation: -90, defaultHeight: M.DEFAULT_HEIGHT, maxHeight: M.MAX_HEIGHT }) });
  }
  function crc32(bytes) {
    let crc = 0xffffffff;
    for (const byte of bytes) { crc ^= byte; for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0); }
    return (crc ^ 0xffffffff) >>> 0;
  }
  function withDpi(png, dpi) {
    if (!(png instanceof Uint8Array) || png.length < 33 || ![137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => png[index] === byte)) throw new TypeError('A valid PNG is required.');
    const density = Math.round(dpi / .0254), phys = new Uint8Array(21), view = new DataView(phys.buffer);
    view.setUint32(0, 9); phys.set([112, 72, 89, 115], 4); view.setUint32(8, density); view.setUint32(12, density); phys[16] = 1; view.setUint32(17, crc32(phys.subarray(4, 17)));
    const chunks = [png.subarray(0, 8)], input = new DataView(png.buffer, png.byteOffset, png.byteLength);
    let offset = 8, inserted = false, ended = false;
    while (offset + 12 <= png.length) {
      const length = input.getUint32(offset), end = offset + length + 12;
      if (end > png.length) throw new TypeError('The PNG has an incomplete chunk.');
      const type = String.fromCharCode(...png.subarray(offset + 4, offset + 8));
      if (type !== 'pHYs') chunks.push(png.subarray(offset, end));
      if (type === 'IHDR') { chunks.push(phys); inserted = true; }
      offset = end; if (type === 'IEND') { ended = true; break; }
    }
    if (!inserted || !ended) throw new TypeError('The PNG is missing its header or end chunk.');
    const result = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0)); let cursor = 0;
    for (const chunk of chunks) { result.set(chunk, cursor); cursor += chunk.length; } return result;
  }
  return Object.freeze({ load, withDpi });
});
